import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Briefcase, CheckCircle, Clock, XCircle, FileText, AlertCircle } from "lucide-react";
import { API } from "../constants";
import { useAuth } from "../context/AuthContext";

const WholesalePage = () => {
  const { token, user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    ein: "",
    business_address: "",
    contact_phone: "",
    resale_cert_note: "",
    estimated_monthly_volume: "",
    notes: "",
  });

  useEffect(() => {
    if (authLoading) return;
    if (!token) {
      setLoading(false);
      return;
    }
    axios
      .get(`${API}/wholesale/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Failed to load wholesale status"))
      .finally(() => setLoading(false));
  }, [token, authLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/wholesale/apply`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Application submitted! We'll review within 1-2 business days.");
      const r = await axios.get(`${API}/wholesale/me`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Application failed");
    }
    setSubmitting(false);
  };

  if (!authLoading && !token) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center px-4">
        <div className="text-center">
          <h1 className="font-heading text-3xl mb-4">Login Required</h1>
          <p className="text-gray-400 mb-6">Sign in or create an account to apply for wholesale pricing.</p>
          <Link to="/login" className="bg-[#D62828] hover:bg-[#B91C1C] text-white px-8 py-3 rounded-sm font-bold uppercase tracking-wider">Login</Link>
        </div>
      </div>
    );
  }

  const latestApp = data?.applications?.[0];
  const tierInfo = data?.tiers?.find((t) => t.tier === data?.tier);

  return (
    <div className="min-h-screen bg-[#050505] pt-24" data-testid="wholesale-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-2">
          <Briefcase className="w-8 h-8 text-[#D62828]" />
          <h1 className="font-heading text-5xl tracking-wide">B2B / WHOLESALE</h1>
        </div>
        <p className="text-gray-400 mb-10 max-w-2xl">For shops, detailers, and resellers. Apply once, and unlock tiered volume pricing on every order — plus optional NET-30 invoicing.</p>

        {loading ? (
          <p className="text-gray-400">Loading...</p>
        ) : data?.is_wholesale ? (
          <div className="bg-[#121212] border border-green-500/30 rounded-sm p-6 mb-8" data-testid="wholesale-active">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-400" />
              <h2 className="font-heading text-2xl text-white">Account Approved — {tierInfo?.name || `Tier ${data.tier}`}</h2>
            </div>
            <p className="text-gray-300 mb-3">You receive <b className="text-green-400">{tierInfo?.percent_off || 0}% off</b> on qualifying orders. Pricing applied automatically at checkout.</p>
            <p className="text-gray-400 text-sm">Payment options: Pay now (Stripe/PayPal) or NET-30 invoice.</p>
          </div>
        ) : null}

        {/* Tier table — always visible */}
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6 mb-8">
          <h3 className="font-heading text-xl mb-4">Volume Tiers</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs text-gray-400 uppercase border-b border-white/10">
                <tr>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Order Subtotal</th>
                  <th className="py-2 pr-4">Discount</th>
                </tr>
              </thead>
              <tbody>
                {(data?.tiers || []).map((t) => (
                  <tr key={t.tier} className="border-b border-white/5">
                    <td className="py-3 pr-4 text-white font-semibold">{t.name}</td>
                    <td className="py-3 pr-4 text-gray-300">${t.min_subtotal.toFixed(2)}+</td>
                    <td className="py-3 pr-4 text-green-400 font-semibold">{t.percent_off}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Application form (hidden if already approved) */}
        {!data?.is_wholesale && (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
            {latestApp?.status === "pending" ? (
              <div className="flex items-center gap-3 mb-4" data-testid="wholesale-pending">
                <Clock className="w-6 h-6 text-yellow-400" />
                <div>
                  <h3 className="font-heading text-xl text-white">Application Under Review</h3>
                  <p className="text-gray-400 text-sm">We'll email you within 1-2 business days. Submitted {new Date(latestApp.applied_at).toLocaleDateString()}.</p>
                </div>
              </div>
            ) : latestApp?.status === "rejected" ? (
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2" data-testid="wholesale-rejected">
                  <XCircle className="w-6 h-6 text-red-400" />
                  <h3 className="font-heading text-xl text-white">Previous Application Not Approved</h3>
                </div>
                {latestApp.review_notes && (
                  <p className="text-sm text-gray-400 mb-4 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-gray-500 mt-0.5" /> {latestApp.review_notes}
                  </p>
                )}
                <p className="text-gray-300 text-sm">You may submit a new application below with updated info.</p>
              </div>
            ) : (
              <h3 className="font-heading text-2xl mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#D62828]" /> Apply for Wholesale Pricing
              </h3>
            )}

            {(!latestApp || latestApp.status !== "pending") && (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="wholesale-form">
                <input type="text" placeholder="Business Name" required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 rounded-sm h-12 px-4" data-testid="wholesale-business-name" />
                <input type="text" placeholder="EIN / Tax ID" required value={form.ein} onChange={(e) => setForm({ ...form, ein: e.target.value })} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 rounded-sm h-12 px-4" data-testid="wholesale-ein" />
                <input type="text" placeholder="Business Address" required value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 rounded-sm h-12 px-4" data-testid="wholesale-address" />
                <input type="tel" placeholder="Contact Phone" required value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 rounded-sm h-12 px-4" data-testid="wholesale-phone" />
                <div>
                  <textarea placeholder="Resale Certificate — paste a link to your cert OR write the certificate number" rows={3} value={form.resale_cert_note} onChange={(e) => setForm({ ...form, resale_cert_note: e.target.value })} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 rounded-sm p-4" data-testid="wholesale-cert-note" />
                  <p className="text-xs text-gray-500 mt-1">Or email a PDF of your resale cert to <a href="mailto:sales@cutabovepolishshop.online" className="text-[#D62828] hover:underline">sales@cutabovepolishshop.online</a> with subject "Wholesale Application — [Your Business]".</p>
                </div>
                <input type="text" placeholder="Estimated monthly volume (optional)" value={form.estimated_monthly_volume} onChange={(e) => setForm({ ...form, estimated_monthly_volume: e.target.value })} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 rounded-sm h-12 px-4" data-testid="wholesale-volume" />
                <textarea placeholder="Notes (optional)" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full bg-[#0F0F0F] border border-white/10 text-white placeholder:text-gray-600 rounded-sm p-4" data-testid="wholesale-notes" />
                <button type="submit" disabled={submitting} className="w-full bg-[#D62828] hover:bg-[#B91C1C] text-white py-3 rounded-sm font-bold uppercase tracking-wider transition-all btn-glow disabled:opacity-50" data-testid="wholesale-submit">
                  {submitting ? "Submitting..." : "Submit Application"}
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WholesalePage;
